import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Collapse,
  Button,
  Progress,
  Table,
  Spinner,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import {
  FaMoneyBillWave,
  FaChartPie,
  FaBalanceScale,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaBuilding,
  FaQrcode,
  FaMobileAlt,
  FaMoneyCheckAlt,
  FaCreditCard,
  FaBookOpen,
  FaFileInvoiceDollar,
  FaStethoscope,
} from "react-icons/fa";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  getFinanceDashboardData,
} from "helpers/backend_helper";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#64748b";
const COLOR_SUCCESS = "#16a34a";
const COLOR_DANGER = "#dc2626";
const COLOR_INDIGO = "#4f46e5";

// ─── Chart Color Palettes ────────────────────────────────────────────────────
const CHART_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ec4899", "#8b5cf6",
  "#06b6d4", "#f97316", "#14b8a6", "#6366f1", "#84cc16",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  `₹${Number(Math.round(Number(amount || 0))).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}`;

const calculatePercentage = (paid, total) => {
  const p = Number(paid || 0);
  const t = Number(total || 0);
  return t === 0 ? 0 : ((p / t) * 100).toFixed(1);
};

// ─── Center Palette Generator (3 clean, professional options) ────────────────
const CENTER_PALETTES = [
  { bg: "#f0fdf4", border: "#bbf7d0", iconColor: "#16a34a" },
  { bg: "#eef2ff", border: "#c7d2fe", iconColor: "#4f46e5" },
  { bg: "#fef2f2", border: "#fecaca", iconColor: "#dc2626" },
  { bg: "#f8fafc", border: "#e2e8f0", iconColor: "#475569" },
  { bg: "#f0fdf4", border: "#bbf7d0", iconColor: "#16a34a" },
  { bg: "#eef2ff", border: "#c7d2fe", iconColor: "#4f46e5" },
];

// ─── Main Academic Fees Dashboard ─────────────────────────────────────────────
const AcademicFeesDashboard = ({ aYear }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedCenter, setExpandedCenter] = useState(null);
  const [showAllCenters, setShowAllCenters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(aYear);
  const [activeDrawer, setActiveDrawer] = useState(null);

  useEffect(() => {
    setSelectedYear(aYear);
  }, [aYear]);

  useEffect(() => {
    if (!selectedYear) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const financeRes = await getFinanceDashboardData({ academic_year: selectedYear });

        if (financeRes?.data.success) {
          setDashboardData(financeRes.data.data);
          const firstCenter = financeRes.data.data.centers?.[0];
          if (firstCenter) setExpandedCenter(firstCenter.center_id);
        } else {
          console.error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: 320 }}>
        <Spinner color="success" style={{ width: "2.5rem", height: "2.5rem" }} />
        <p className="text-muted mt-3 mb-0 fs-6">Loading fees dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center text-danger py-5 fw-semibold">
        Failed to load dashboard data. Please try again or refresh the page.
      </div>
    );
  }

  const { overall, centers = [] } = dashboardData;

  // ── Exact Business Logic Calculations ──────────────────────────────────────
  const expectedTermFee = Number(overall.term_total_amount);
  const skippedTermFee = Number(overall.term_skipped_amount || 0);

  const totalCollected =
    Number(overall.term_paid_amount) +
    Number(overall.term_historical_paid_amount) +
    Number(overall.otherfees_paid_amount) +
    Number(overall.observation_paid_amount) +
    Number(overall.registeration_paid_amount) +
    Number(overall.term_latefee_paid_amount) +
    Number(overall.otherfees_latefee_paid_amount);

  const totalAmount =
    expectedTermFee +
    skippedTermFee +
    Number(overall.term_expected_latefee_amount) +
    Number(overall.otherfees_total_amount) +
    Number(overall.observation_paid_amount) +
    Number(overall.registeration_paid_amount) +
    Number(overall.term_historical_paid_amount) +
    Number(overall.term_historical_latefee_paid_amount || 0) -
    Number(overall.term_expected_discount);

  const totalBalance = Math.max(0, totalAmount - totalCollected);

  const totalLateFee =
    Number(overall.term_latefee_paid_amount) + Number(overall.otherfees_latefee_paid_amount);

  const totalPaymentChannelAmount = Object.values(overall.payment_channels || {}).reduce(
    (sum, val) => sum + Number(val),
    0
  );

  const otherfeesExpectedLatefeeAmount =
    overall.otherfees_expected_latefee_amount !== undefined
      ? Number(overall.otherfees_expected_latefee_amount)
      : (centers || []).reduce((sum, c) => sum + Number(c.otherfees_expected_latefee_amount || 0), 0);

  const termLateFeeBalance = Math.max(
    0,
    Number(overall.term_expected_latefee_amount || 0) - Number(overall.term_latefee_paid_amount || 0)
  );
  const otherLateFeeBalance = Math.max(
    0,
    Number(otherfeesExpectedLatefeeAmount) - Number(overall.otherfees_latefee_paid_amount || 0)
  );
  const totalLateFeeBalance = termLateFeeBalance + otherLateFeeBalance;
  const termBaseBalance = Math.max(0, Number(overall.term_balance_amount || 0) - termLateFeeBalance);
  const otherBaseBalance = Math.max(0, Number(overall.otherfees_balance_amount || 0) - otherLateFeeBalance);

  const overallPercent = calculatePercentage(totalCollected, totalAmount);

  // ── Tooltip Breakdowns (Refined for maximum clarity) ───────────────────────
  const collectedTooltipRows = [
    {
      label: "Term Fees Paid",
      value: formatCurrency(
        Number(overall.term_paid_amount) + Number(overall.term_historical_paid_amount)
      ),
      dotColor: "#16a34a",
    },
    {
      label: "Other Fees Paid",
      value: formatCurrency(overall.otherfees_paid_amount),
      dotColor: "#16a34a",
    },
    {
      label: "Late Fees Paid",
      value: formatCurrency(
        Number(overall.term_latefee_paid_amount) + Number(overall.otherfees_latefee_paid_amount)
      ),
      dotColor: "#4f46e5",
    },
    {
      label: "Registration Fees",
      value: formatCurrency(overall.registeration_paid_amount),
      dotColor: "#64748b",
    },
    {
      label: "Observation Fees",
      value: formatCurrency(overall.observation_paid_amount),
      dotColor: "#64748b",
    },
  ];

  const billedTooltipRows = [
    {
      label: "Term Fees (Base Expected)",
      value: `+${formatCurrency(expectedTermFee)}`,
      dotColor: "#4f46e5",
      badge: "+ Addition",
      badgeColor: "#eef2ff",
      badgeTextColor: "#4f46e5",
      subNote: "Base student tuition fees billed for the academic year",
    },
    ...(skippedTermFee > 0
      ? [
        {
          label: "Skipped Term Amount",
          value: `+${formatCurrency(skippedTermFee)}`,
          badge: "+ Addition",
          badgeColor: "#eef2ff",
          badgeTextColor: "#4f46e5",
          dotColor: "#4f46e5",
          subNote: "Additional terms included in billing cycle",
        },
      ]
      : []),
    {
      label: "Other Fees (Activities & Transport)",
      value: `+${formatCurrency(
        Number(overall.otherfees_total_amount) - Number(otherfeesExpectedLatefeeAmount)
      )}`,
      badge: "+ Addition",
      badgeColor: "#eef2ff",
      badgeTextColor: "#4f46e5",
      dotColor: "#4f46e5",
      subNote: "Non-tuition activity, materials and transport billed",
    },
    {
      label: "Expected Late Fee",
      value: `+${formatCurrency(
        Number(overall.term_expected_latefee_amount) + Number(otherfeesExpectedLatefeeAmount)
      )}`,
      badge: "+ Addition",
      badgeColor: "#eef2ff",
      badgeTextColor: "#4f46e5",
      dotColor: "#4f46e5",
      subNote: "Fines & late payment charges accrued",
    },
    {
      label: "Historical Paid (Prior Receipts)",
      value: `+${formatCurrency(overall.term_historical_paid_amount)}`,
      badge: "+ Addition",
      badgeColor: "#f8fafc",
      badgeTextColor: "#64748b",
      dotColor: "#64748b",
      subNote: "Prior academic year fees accounted in this cycle",
    },
    ...(Number(overall.term_historical_latefee_paid_amount) > 0
      ? [
        {
          label: "Historical Late Fee Paid",
          value: `+${formatCurrency(overall.term_historical_latefee_paid_amount)}`,
          badge: "+ Addition",
          badgeColor: "#f8fafc",
          badgeTextColor: "#64748b",
          dotColor: "#64748b",
          subNote: "Prior late fee collections credited",
        },
      ]
      : []),
    {
      label: "Registration Fees",
      value: `+${formatCurrency(overall.registeration_paid_amount)}`,
      badge: "+ Addition",
      badgeColor: "#f8fafc",
      badgeTextColor: "#64748b",
      dotColor: "#64748b",
      subNote: "One-time student onboarding charges",
    },
    {
      label: "Observation Fees",
      value: `+${formatCurrency(overall.observation_paid_amount)}`,
      badge: "+ Addition",
      badgeColor: "#f8fafc",
      badgeTextColor: "#64748b",
      dotColor: "#64748b",
      subNote: "Direct admission & assessment charges",
    },
    {
      label: "Expected Discount & Concessions",
      value: `-${formatCurrency(overall.term_expected_discount)}`,
      badge: "− Deduction",
      badgeColor: "#fef2f2",
      badgeTextColor: "#dc2626",
      dotColor: "#dc2626",
      subNote: "Discounts and fee concessions subtracted from gross receivable",
      isDeduction: true,
    },
  ];

  const expectedLateFeeTotal =
    Number(overall.term_expected_latefee_amount) + Number(otherfeesExpectedLatefeeAmount);

  const balanceTooltipRows = [
    {
      label: "Term Fees Balance",
      value: formatCurrency(termBaseBalance),
      dotColor: "#dc2626",
    },
    {
      label: "Other Fees Balance",
      value: formatCurrency(otherBaseBalance),
      dotColor: "#dc2626",
    },
    ...(expectedLateFeeTotal > 0
      ? [
        {
          label: "Late Fee Expected",
          value: formatCurrency(expectedLateFeeTotal),
          dotColor: "#ea580c",
          badge: "+ Accrued",
          badgeColor: "#fff7ed",
          badgeTextColor: "#ea580c",
        },
      ]
      : []),
    ...(totalLateFee > 0
      ? [
        {
          label: "Late Fee Paid",
          value: formatCurrency(totalLateFee),
          dotColor: "#16a34a",
        },
      ]
      : []),
    ...(totalLateFeeBalance > 0
      ? [
        {
          label: "Late Fee Unpaid",
          value: formatCurrency(totalLateFeeBalance),
          dotColor: "#dc2626",
        },
      ]
      : []),
  ];

  // Helper for Payment Channel Icon & Color
  const getChannelStyle = (channel, index) => {
    const ch = channel.toUpperCase();
    if (ch.includes("MIP")) {
      return {
        bg: "#eef2ff",
        border: "#c7d2fe",
        iconBg: "#4f46e5",
        icon: <FaQrcode color="#ffffff" size={13} />,
      };
    }
    if (ch.includes("DIRECT") || ch.includes("SCREENSHOT")) {
      return {
        bg: "#eef2ff",
        border: "#c7d2fe",
        iconBg: "#4f46e5",
        icon: <FaMobileAlt color="#ffffff" size={13} />,
      };
    }
    if (ch.includes("NDM") || ch.includes("MOM")) {
      return {
        bg: "#f0fdf4",
        border: "#bbf7d0",
        iconBg: "#16a34a",
        icon: <FaMoneyCheckAlt color="#ffffff" size={13} />,
      };
    }
    const defaultStyles = [
      { bg: "#eef2ff", border: "#c7d2fe", iconBg: "#4f46e5", icon: <FaQrcode color="#ffffff" size={13} /> },
      { bg: "#eef2ff", border: "#c7d2fe", iconBg: "#4f46e5", icon: <FaMobileAlt color="#ffffff" size={13} /> },
      { bg: "#f0fdf4", border: "#bbf7d0", iconBg: "#16a34a", icon: <FaMoneyCheckAlt color="#ffffff" size={13} /> },
      { bg: "#f8fafc", border: "#e2e8f0", iconBg: "#64748b", icon: <FaCreditCard color="#ffffff" size={13} /> },
    ];
    return defaultStyles[index % defaultStyles.length];
  };

  const displayedCenters = showAllCenters ? centers : centers.slice(0, 5);

  // Drawer Configuration Mapping
  const drawerConfigs = {
    collected: {
      title: "Collected Fees Breakdown",
      subtitle: "Detailed breakdown of all received fee collections",
      totalLabel: "Total Collected",
      totalValue: formatCurrency(totalCollected),
      subText: `${overallPercent}% of total expected (${formatCurrency(totalAmount)})`,
      theme: "green",
      icon: <FaMoneyBillWave size={20} />,
      rows: collectedTooltipRows,
      accentColor: "#16a34a",
      bgLight: "#f0fdf4",
      borderColor: "#bbf7d0",
    },
    billed: {
      title: "Total Expected Breakdown",
      subtitle: "Detailed breakdown of all expected and invoiced fees",
      totalLabel: "Total Expected",
      totalValue: formatCurrency(totalAmount),
      subText: `Total expected amount across all categories and centers`,
      theme: "orange",
      icon: <FaChartPie size={20} />,
      rows: billedTooltipRows,
      accentColor: "#ea580c",
      bgLight: "#fff7ed",
      borderColor: "#fed7aa",
    },
    balance: {
      title: "Balance Due Breakdown",
      subtitle: "Detailed breakdown of outstanding fee balances",
      totalLabel: "Total Balance Due",
      totalValue: formatCurrency(totalBalance),
      subText: `${(100 - Number(overallPercent)).toFixed(1)}% remaining balance to be collected`,
      theme: "red",
      icon: <FaBalanceScale size={20} />,
      rows: balanceTooltipRows,
      accentColor: "#dc2626",
      bgLight: "#fef2f2",
      borderColor: "#fecaca",
    },
  };

  const currentDrawer = activeDrawer ? drawerConfigs[activeDrawer] : null;

  return (
    <Container fluid className="px-0">

      {/* ── 1. Top KPI Summary Metrics ─────────────────────────────────────── */}
      <Row className="mb-0 g-2">
        {[
          {
            key: "billed",
            title: "Total Expected",
            amount: totalAmount,
            pill: "100%",
            theme: {
              bg: "#f8fbff",
              border: "#dbeafe",
              title: "#2563eb",
              pillBg: "#eff6ff",
              pillText: "#2563eb",
              pillBorder: "#dbeafe",
              buttonText: "#2563eb",
              buttonBorder: "#bfdbfe",
            },
          },
          {
            key: "collected",
            title: "Total Collected",
            amount: totalCollected,
            pill: `${overallPercent}%`,
            theme: {
              bg: "#f7fdf9",
              border: "#bbf7d0",
              title: "#16a34a",
              pillBg: "#f0fdf4",
              pillText: "#16a34a",
              pillBorder: "#bbf7d0",
              buttonText: "#16a34a",
              buttonBorder: "#86efac",
            },
          },
          {
            key: "balance",
            title: "Balance Due",
            amount: totalBalance,
            pill: `${(100 - Number(overallPercent)).toFixed(1)}%`,
            theme: {
              bg: "#fff8f8",
              border: "#fecaca",
              title: "#dc2626",
              pillBg: "#fef2f2",
              pillText: "#dc2626",
              pillBorder: "#fecaca",
              buttonText: "#dc2626",
              buttonBorder: "#fecaca",
            },
          },
        ].map((item) => (
          <Col key={item.key} md={4}>
            <Card
              className="border-0"
              style={{
                borderRadius: "12px",
                border: `1px solid ${item.theme.border}`,
                boxShadow: "0 1px 4px rgba(15, 23, 42, 0.04)",
                background: item.theme.bg,
                overflow: "hidden",
                transition: "box-shadow 0.15s ease, transform 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(15, 23, 42, 0.08)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(15, 23, 42, 0.04)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <CardBody className="p-3">
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <div>
                    <div style={{ fontSize: "0.84rem", fontWeight: 700, color: item.theme.title }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "#0f172a", marginTop: "6px", letterSpacing: "-0.3px" }}>
                      {formatCurrency(item.amount)}
                    </div>
                  </div>
                  <div className="d-flex flex-column align-items-end" style={{ gap: "6px" }}>
                    <span
                      style={{
                        minWidth: 80,
                        textAlign: "center",
                        padding: "3px 8px",
                        borderRadius: "20px",
                        background: item.theme.pillBg,
                        color: item.theme.pillText,
                        border: `1px solid ${item.theme.pillBorder}`,
                        fontSize: "0.66rem",
                        fontWeight: 700,
                      }}
                    >
                      {item.pill}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveDrawer(item.key)}
                      className="d-inline-flex align-items-center gap-1.5"
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: `1px solid ${item.theme.buttonBorder}`,
                        background: "#ffffff",
                        color: item.theme.buttonText,
                        fontSize: "0.66rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s ease",
                      }}
                    >
                      View breakdown <FaChevronRight size={8} />
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Side Offcanvas Breakdown Drawer ────────────────────────────────── */}
      <Offcanvas
        isOpen={Boolean(activeDrawer)}
        toggle={() => setActiveDrawer(null)}
        direction="end"
        style={{
          width: "440px",
          maxWidth: "92vw",
          boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.15)",
          border: "none",
          zIndex: 1060,
        }}
      >
        {currentDrawer && (
          <div className="d-flex flex-column h-100 bg-white">
            {/* Drawer Header */}
            <OffcanvasHeader
              toggle={() => setActiveDrawer(null)}
              className="border-bottom p-3"
            >
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "8px",
                    background: currentDrawer.bgLight,
                    border: `1px solid ${currentDrawer.borderColor}`,
                    color: currentDrawer.accentColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {currentDrawer.icon}
                </span>
                <div>
                  <h5 className="fw-bold mb-0" style={{ fontSize: "1rem", color: "#0f172a" }}>
                    {currentDrawer.title}
                  </h5>
                </div>
              </div>
            </OffcanvasHeader>

            <OffcanvasBody className="p-0 d-flex flex-column" style={{ overflowY: "auto" }}>
              {/* Quick Switch Tabs inside Drawer */}
              <div
                style={{
                  padding: "10px 16px",
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "6px",
                }}
              >
                {[
                  { key: "billed", label: "Expected", color: "#ea580c" },
                  { key: "collected", label: "Collected", color: "#16a34a" },
                  { key: "balance", label: "Balance Due", color: "#dc2626" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveDrawer(tab.key)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      borderRadius: "8px",
                      fontSize: "0.76rem",
                      fontWeight: activeDrawer === tab.key ? 700 : 500,
                      border: activeDrawer === tab.key ? `1px solid ${tab.color}` : "1px solid #e2e8f0",
                      background: activeDrawer === tab.key ? "#ffffff" : "transparent",
                      color: activeDrawer === tab.key ? tab.color : "#64748b",
                      boxShadow: activeDrawer === tab.key ? "0 1px 3px rgba(0,0,0,0.05)" : "none",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Drawer Content */}
              <div style={{ padding: "18px 20px", flex: 1 }}>
                {/* Hero Metric Box */}
                <div
                  style={{
                    background: currentDrawer.bgLight,
                    border: `1px solid ${currentDrawer.borderColor}`,
                    borderRadius: "12px",
                    padding: "14px 16px",
                    marginBottom: "16px",
                  }}
                >
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>
                    {currentDrawer.totalLabel}
                  </div>
                  <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#0f172a", marginTop: "2px", letterSpacing: "-0.5px" }}>
                    {currentDrawer.totalValue}
                  </div>
                  <div style={{ fontSize: "0.76rem", color: currentDrawer.accentColor, fontWeight: 600, marginTop: "4px" }}>
                    {currentDrawer.subText}
                  </div>
                </div>

                {/* Itemized Breakdown List */}
                <div className="mb-2" style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  Fee Components Breakdown
                </div>

                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                  }}
                >
                  {currentDrawer.rows.map((row, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "11px 14px",
                        borderBottom: idx === currentDrawer.rows.length - 1 ? "none" : "1px solid #f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#ffffff")}
                    >
                      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: row.dotColor || currentDrawer.accentColor,
                            flexShrink: 0,
                          }}
                        ></span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.label}
                          </div>
                          {row.subNote && (
                            <div style={{ fontSize: "0.68rem", color: "#64748b", marginTop: 2 }}>
                              {row.subNote}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-end ps-2 flex-shrink-0">
                        <div className="d-flex align-items-center justify-content-end gap-1.5">
                          {row.badge && (
                            <span
                              style={{
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                padding: "2px 5px",
                                borderRadius: "4px",
                                background: row.badgeColor || "#f1f5f9",
                                color: row.badgeTextColor || "#475569",
                              }}
                            >
                              {row.badge}
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: "0.84rem",
                              fontWeight: 700,
                              color: row.isDeduction ? "#dc2626" : "#0f172a",
                              fontFamily: "monospace, sans-serif",
                            }}
                          >
                            {row.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Detailed Fee Breakdowns (Balance Due only) */}
                {activeDrawer === "balance" && (
                  <div className="mt-4 d-flex flex-column gap-3">
                    <div className="mb-1" style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      Detailed Fee Breakdowns
                    </div>

                    {/* Term Fees Breakdown */}
                    {(() => {
                      const termBilled = Number(overall.term_total_amount || 0) + Number(overall.term_skipped_amount || 0);
                      const termDiscount = Number(overall.term_expected_discount || 0);
                      const termRefund = Number(overall.term_refund_amount || 0);
                      const termPaid = Number(overall.term_paid_amount || 0);
                      const termHistPaid = Number(overall.term_historical_paid_amount || 0);
                      const termLateTotal = Number(overall.term_expected_latefee_amount || 0);
                      const termLatePaid = Number(overall.term_latefee_paid_amount || 0);
                      const termLateRefund = Number(overall.term_late_refund_amount || 0);
                      const termLateUnpaid = Math.max(0, termLateTotal - termLatePaid - termLateRefund);
                      const termBal = Number(overall.term_balance_amount || 0);
                      const isSettled = termBal <= 0;

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "#ffffff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 28, height: 28, borderRadius: "8px", background: "#eff6ff", color: "#3b82f6", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaBookOpen size={13} />
                              </span>
                              <span className="fw-bold" style={{ fontSize: "0.85rem", color: "#0f172a" }}>
                                Term Fees Breakdown
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "#f0fdf4" : "#fef2f2",
                                color: isSettled ? "#16a34a" : "#dc2626",
                                border: isSettled ? "1px solid #bbf7d0" : "1px solid #fecaca",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#475569" }}>Total Amount Billed</span>
                              <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(termBilled)}</span>
                            </div>

                            {Number(overall.term_skipped_amount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#2563eb" }}>Skipped Term Added</span>
                                <span className="fw-bold" style={{ color: "#2563eb" }}>{formatCurrency(overall.term_skipped_amount)}</span>
                              </div>
                            )}

                            {termDiscount > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Concession Discount</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(termDiscount)}</span>
                              </div>
                            )}

                            {termRefund > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Refund Processed</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(termRefund)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#16a34a", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(termPaid)}</span>
                            </div>

                            {termHistPaid > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#2563eb" }}>Historical Paid</span>
                                <span className="fw-bold" style={{ color: "#2563eb" }}>{formatCurrency(termHistPaid)}</span>
                              </div>
                            )}

                            {termLateTotal > 0 && (
                              <div className="py-1 px-1 mt-0.5">
                                <div className="d-flex justify-content-between mb-0.5">
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>Total Late Fee</span>
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>{formatCurrency(termLateTotal)}</span>
                                </div>
                                <div className="d-flex flex-wrap justify-content-between ps-2 text-muted" style={{ fontSize: "0.72rem", gap: "6px" }}>
                                  <span>– Paid: <strong style={{ color: "#16a34a" }}>{formatCurrency(termLatePaid)}</strong></span>
                                  {termLateRefund > 0 && (
                                    <span>– Refunded: <strong style={{ color: "#0f172a" }}>{formatCurrency(termLateRefund)}</strong></span>
                                  )}
                                  <span>– Unpaid: <strong style={{ color: termLateUnpaid > 0 ? "#dc2626" : "#64748b" }}>{formatCurrency(termLateUnpaid)}</strong></span>
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "#e2e8f0" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626" }}>Remaining Balance</span>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626", fontSize: "0.85rem" }}>{formatCurrency(termBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Other Fees Breakdown */}
                    {(() => {
                      const otherBilled = Number(overall.otherfees_total_amount || 0);
                      const otherRefund = Number(overall.otherfees_refund_amount || 0);
                      const otherPaid = Number(overall.otherfees_paid_amount || 0);
                      const otherLateTotal = Number(overall.otherfees_expected_latefee_amount || otherfeesExpectedLatefeeAmount);
                      const otherLatePaid = Number(overall.otherfees_latefee_paid_amount || 0);
                      const otherLateRefund = Number(overall.otherfees_late_refund_amount || 0);
                      const otherLateUnpaid = Math.max(0, otherLateTotal - otherLatePaid - otherLateRefund);
                      const otherBal = Number(overall.otherfees_balance_amount || 0);
                      const isSettled = otherBal <= 0;

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "#ffffff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 28, height: 28, borderRadius: "8px", background: "#fff7ed", color: "#ea580c", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaFileInvoiceDollar size={13} />
                              </span>
                              <span className="fw-bold" style={{ fontSize: "0.85rem", color: "#0f172a" }}>
                                Other Fees Breakdown
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "#f0fdf4" : "#fef2f2",
                                color: isSettled ? "#16a34a" : "#dc2626",
                                border: isSettled ? "1px solid #bbf7d0" : "1px solid #fecaca",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#475569" }}>Total Amount Billed</span>
                              <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(otherBilled)}</span>
                            </div>

                            {otherRefund > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Refund Processed</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(otherRefund)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#16a34a", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(otherPaid)}</span>
                            </div>

                            {otherLateTotal > 0 && (
                              <div className="py-1 px-1 mt-0.5">
                                <div className="d-flex justify-content-between mb-0.5">
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>Total Late Fee</span>
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>{formatCurrency(otherLateTotal)}</span>
                                </div>
                                <div className="d-flex flex-wrap justify-content-between ps-2 text-muted" style={{ fontSize: "0.72rem", gap: "6px" }}>
                                  <span>– Paid: <strong style={{ color: "#16a34a" }}>{formatCurrency(otherLatePaid)}</strong></span>
                                  {otherLateRefund > 0 && (
                                    <span>– Refunded: <strong style={{ color: "#0f172a" }}>{formatCurrency(otherLateRefund)}</strong></span>
                                  )}
                                  <span>– Unpaid: <strong style={{ color: otherLateUnpaid > 0 ? "#dc2626" : "#64748b" }}>{formatCurrency(otherLateUnpaid)}</strong></span>
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "#e2e8f0" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626" }}>Remaining Balance</span>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626", fontSize: "0.85rem" }}>{formatCurrency(otherBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Total Summary Footer Box */}
                <div
                  style={{
                    marginTop: "14px",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    background: currentDrawer.bgLight,
                    border: `1px solid ${currentDrawer.borderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", color: currentDrawer.accentColor, letterSpacing: "0.4px" }}>
                    {currentDrawer.totalLabel}
                  </span>
                  <span style={{ fontSize: "0.98rem", fontWeight: 800, color: currentDrawer.accentColor, fontFamily: "monospace, sans-serif" }}>
                    {currentDrawer.totalValue}
                  </span>
                </div>
              </div>

              {/* Drawer Footer */}
              <div
                style={{
                  padding: "14px 20px",
                  borderTop: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveDrawer(null)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    color: "#334155",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#e2e8f0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#f8fafc";
                  }}
                >
                  Close Breakdown Panel
                </button>
              </div>
            </OffcanvasBody>
          </div>
        )}
      </Offcanvas>

      {/* ── 2. Overall Payment Channels ────────────────────────────────────── */}
      <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: "12px", border: "1px solid #f1f5f9", background: "#ffffff" }}>
        <CardBody className="py-2.5 px-3.5">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="fw-bold" style={{ fontSize: "0.82rem", color: TEXT_DARK }}>
              Overall Payment Channels
            </span>
            <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>
              Total: <strong style={{ color: TEXT_DARK }}>{formatCurrency(totalPaymentChannelAmount)}</strong>
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(overall.payment_channels || {}).map(([channel, amount], i) => {
              const style = getChannelStyle(channel, i);
              return (
                <div
                  key={i}
                  className="flex-fill d-flex align-items-center gap-2"
                  style={{
                    minWidth: "130px",
                    background: style.bg,
                    border: `1px solid ${style.border}`,
                    borderRadius: "10px",
                    padding: "7px 10px",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      background: style.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {style.icon}
                  </div>
                  <div style={{ overflow: "hidden" }}>
                    <div
                      className="text-uppercase fw-semibold"
                      style={{
                        fontSize: "0.62rem",
                        color: "#64748b",
                        letterSpacing: "0.3px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={channel}
                    >
                      {channel}
                    </div>
                    <div className="fw-bold" style={{ fontSize: "0.84rem", color: TEXT_DARK, lineHeight: 1.2 }}>
                      {formatCurrency(amount)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* ── 3. Center-wise Fee Collection Section Header ────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 px-1">
        <h5 className="fw-bold mb-0" style={{ fontSize: "0.98rem", color: TEXT_DARK }}>
          Center-wise Fee Collection
        </h5>
        <span style={{ fontSize: "0.8rem", color: TEXT_MUTED }}>
          {centers.length} {centers.length === 1 ? "Center" : "Centers"} Total
        </span>
      </div>

      {/* ── 4. Center Cards List ───────────────────────────────────────────── */}
      {displayedCenters.map((center, cIndex) => {
        const collected =
          Number(center.term_paid_amount) +
          Number(center.otherfees_paid_amount) +
          Number(center.observation_paid_amount) +
          Number(center.registeration_paid_amount) +
          Number(center.term_latefee_paid_amount || 0) +
          Number(center.otherfees_latefee_paid_amount || 0);

        const totalAmt =
          Number(center.term_total_amount) +
          Number(center.term_expected_latefee_amount) +
          Number(center.otherfees_total_amount) -
          Number(center.term_refund_amount) -
          Number(center.otherfees_refund_amount);

        const collectionPercent = calculatePercentage(collected, totalAmt);
        const isExpanded = expandedCenter === center.center_id;

        const totalChannel = Object.values(center.payment_channels || {}).reduce(
          (sum, val) => sum + Number(val),
          0
        );

        const palette = CENTER_PALETTES[cIndex % CENTER_PALETTES.length];

        return (
          <Card
            key={center.center_id}
            className="border-0 shadow-sm mb-2"
            style={{
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              background: "#ffffff",
              transition: "box-shadow 0.2s ease",
            }}
          >
            {/* Center Summary Banner */}
            <div
              className="p-3 bg-white"
              style={{ cursor: "pointer" }}
              onClick={() => setExpandedCenter(isExpanded ? null : center.center_id)}
            >
              <Row className="align-items-center g-4">
                {/* Center Title & Location */}
                <Col xs={12} sm={4} md={3} className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: palette.bg,
                      border: `1px solid ${palette.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FaBuilding color={palette.iconColor} size={16} />
                  </div>
                  <div>
                    <div
                      className="fw-bold text-uppercase"
                      style={{ fontSize: "0.92rem", color: TEXT_DARK, letterSpacing: "0.3px" }}
                    >
                      {center.center_name || "Unknown Center"}
                    </div>
                  </div>
                </Col>

                {/* Key Amounts Summary */}
                <Col xs={12} sm={5} md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
                    <div>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>Total Expected</div>
                      <div className="fw-bold" style={{ fontSize: "0.92rem", color: "#0f172a", letterSpacing: "-0.2px" }}>
                        {formatCurrency(totalAmt)}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>Term + Other Paid</div>
                      <div className="fw-semibold" style={{ fontSize: "0.92rem", color: TEXT_DARK, letterSpacing: "-0.2px" }}>
                        {formatCurrency(
                          Number(center.term_paid_amount || 0) + Number(center.otherfees_paid_amount || 0)
                        )}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>Late Fee Paid</div>
                      <div className="fw-semibold" style={{ fontSize: "0.92rem", color: COLOR_INDIGO, letterSpacing: "-0.2px" }}>
                        {formatCurrency(
                          Number(center.term_latefee_paid_amount || 0) +
                          Number(center.otherfees_latefee_paid_amount || 0)
                        )}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>Overall Paid</div>
                      <div className="fw-bold" style={{ fontSize: "0.92rem", color: COLOR_SUCCESS, letterSpacing: "-0.2px" }}>
                        {formatCurrency(collected)}
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Collection Progress & Action Chevron */}
                <Col xs={12} sm={3} md={3} className="text-end d-flex align-items-center justify-content-end gap-4">
                  <div>
                    <div className="fw-bold" style={{ fontSize: "1rem", color: COLOR_SUCCESS, letterSpacing: "-0.3px" }}>
                      {formatCurrency(collected)}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-0.5 justify-content-end">
                      <Progress
                        value={collectionPercent}
                        color="success"
                        style={{ width: "80px", height: "4px", borderRadius: "2px", backgroundColor: "#e2e8f0" }}
                      />
                      <span className="fw-semibold" style={{ fontSize: "0.72rem", color: TEXT_MUTED }}>
                        {collectionPercent}%
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: isExpanded ? "#f1f5f9" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#64748b",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Collapsible Detailed View */}
            <Collapse isOpen={isExpanded}>
              <div className="p-4" style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                <Row className="g-3">
                  {/* ── Left Column: Detailed Fee Ledgers ────────────────────── */}
                  <Col xs={12} lg={7} className="d-flex flex-column gap-3">
                    {/* 1. Term Fees Card */}
                    {(() => {
                      const termBilled = Number(center.term_total_amount || 0) + Number(center.term_skipped_amount || 0);
                      const termAdj = Number(center.term_expected_discount || 0) + Number(center.term_refund_amount || 0);
                      const termLatePaid = Math.max(0, Number(center.term_latefee_paid_amount || 0));
                      const termLateRefund = Number(center.term_late_refund_amount || 0);
                      const termLateTotal = Number(center.term_expected_latefee_amount || 0);
                      const termLateUnpaid = Math.max(0, termLateTotal - termLatePaid - termLateRefund);
                      const termPaidTotal = Number(center.term_paid_amount || 0) + Number(center.term_historical_paid_amount || 0);
                      const termBal = Math.max(0, Number(center.term_balance_amount || 0));
                      const isSettled = termBal <= 0;

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2.5 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 28, height: 28, borderRadius: "8px", background: "#eff6ff", color: "#3b82f6", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaBookOpen size={13} />
                              </span>
                              <span className="fw-bold" style={{ fontSize: "0.85rem", color: "#0f172a" }}>
                                Term Fees Breakdown
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "#f0fdf4" : "#fef2f2",
                                color: isSettled ? "#16a34a" : "#dc2626",
                                border: isSettled ? "1px solid #bbf7d0" : "1px solid #fecaca",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#475569" }}>Base Amount Expected</span>
                              <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(center.term_total_amount || 0)}</span>
                            </div>

                            {Number(center.term_skipped_amount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#4f46e5" }}>Skipped Term Added</span>
                                <span className="fw-bold" style={{ color: "#4f46e5" }}>{formatCurrency(center.term_skipped_amount)}</span>
                              </div>
                            )}

                            {Number(center.term_expected_discount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Concession Discount</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(center.term_expected_discount)}</span>
                              </div>
                            )}

                            {Number(center.term_refund_amount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Refund Processed</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(center.term_refund_amount)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#16a34a", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(center.term_paid_amount || 0)}</span>
                            </div>

                            {Number(center.term_historical_paid_amount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#64748b" }}>Historical Paid</span>
                                <span className="fw-bold" style={{ color: "#64748b" }}>{formatCurrency(center.term_historical_paid_amount)}</span>
                              </div>
                            )}

                            {termLateTotal > 0 && (
                              <div className="py-1 px-1 mt-0.5">
                                <div className="d-flex justify-content-between mb-0.5">
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>Late Fee Accrued</span>
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>{formatCurrency(termLateTotal)}</span>
                                </div>
                                <div className="d-flex justify-content-between ps-2 text-muted" style={{ fontSize: "0.72rem" }}>
                                  <span>– Paid: <strong style={{ color: "#16a34a" }}>{formatCurrency(termLatePaid)}</strong></span>
                                  {termLateRefund > 0 && (
                                    <span>– Refunded: <strong style={{ color: "#0f172a" }}>{formatCurrency(termLateRefund)}</strong></span>
                                  )}
                                  <span>– Unpaid: <strong style={{ color: termLateUnpaid > 0 ? "#dc2626" : "#64748b" }}>{formatCurrency(termLateUnpaid)}</strong></span>
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "#e2e8f0" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626" }}>Remaining Balance Due</span>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626", fontSize: "0.85rem" }}>{formatCurrency(termBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 2. Other Fees Card */}
                    {(() => {
                      const otherBilled = Number(center.otherfees_total_amount || 0);
                      const otherPaid = Number(center.otherfees_paid_amount || 0);
                      const otherLatePaid = Math.max(0, Number(center.otherfees_latefee_paid_amount || 0));
                      const otherLateRefund = Number(center.otherfees_late_refund_amount || 0);
                      const otherLateTotal = Number(center.otherfees_expected_latefee_amount || 0);
                      const otherLateUnpaid = Math.max(0, otherLateTotal - otherLatePaid - otherLateRefund);
                      const otherBal = Math.max(0, Number(center.otherfees_balance_amount || 0));
                      const isSettled = otherBal <= 0;

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2.5 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 28, height: 28, borderRadius: "8px", background: "#fff7ed", color: "#ea580c", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaFileInvoiceDollar size={13} />
                              </span>
                              <div>
                                <span className="fw-bold" style={{ fontSize: "0.85rem", color: "#0f172a" }}>
                                  Other Fees Breakdown
                                </span>
                              </div>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "#f0fdf4" : "#fef2f2",
                                color: isSettled ? "#16a34a" : "#dc2626",
                                border: isSettled ? "1px solid #bbf7d0" : "1px solid #fecaca",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#475569" }}>Base Amount Expected</span>
                              <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(otherBilled)}</span>
                            </div>

                            {Number(center.otherfees_refund_amount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Refund Processed</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(center.otherfees_refund_amount)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#16a34a", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(otherPaid)}</span>
                            </div>

                            {otherLateTotal > 0 && (
                              <div className="py-1 px-1 mt-0.5">
                                <div className="d-flex justify-content-between mb-0.5">
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>Late Fee Accrued</span>
                                  <span className="fw-bold" style={{ color: "#ea580c" }}>{formatCurrency(otherLateTotal)}</span>
                                </div>
                                <div className="d-flex justify-content-between ps-2 text-muted" style={{ fontSize: "0.72rem" }}>
                                  <span>– Paid: <strong style={{ color: "#16a34a" }}>{formatCurrency(otherLatePaid)}</strong></span>
                                  {otherLateRefund > 0 && (
                                    <span>– Refunded: <strong style={{ color: "#0f172a" }}>{formatCurrency(otherLateRefund)}</strong></span>
                                  )}
                                  <span>– Unpaid: <strong style={{ color: otherLateUnpaid > 0 ? "#dc2626" : "#64748b" }}>{formatCurrency(otherLateUnpaid)}</strong></span>
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "#e2e8f0" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626" }}>Remaining Balance Due</span>
                              <span className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626", fontSize: "0.85rem" }}>{formatCurrency(otherBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  </Col>

                  {/* ── Right Column: Payment Channels & Overall Summary ───── */}
                  <Col xs={12} lg={5} className="d-flex flex-column gap-3">
                    {/* Card 1: Payment Channels */}
                    <div
                      className="p-3 rounded-3"
                      style={{
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2.5 border-bottom pb-2">
                        <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#0f172a" }}>
                          💳 Payment Channels Received
                        </span>
                        <span className="fw-bold" style={{ fontSize: "0.8rem", color: TEXT_DARK }}>
                          {formatCurrency(totalChannel)}
                        </span>
                      </div>

                      {Object.keys(center.payment_channels || {}).length === 0 ? (
                        <div className="text-center py-3 text-muted" style={{ fontSize: "0.76rem" }}>
                          No payment channel records available.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {Object.entries(center.payment_channels || {}).map(([channel, amount], i) => {
                            const style = getChannelStyle(channel, i);
                            const channelShare = calculatePercentage(amount, totalChannel);
                            return (
                              <div
                                key={i}
                                className="p-2 rounded-3 d-flex align-items-center justify-content-between"
                                style={{
                                  background: style.bg,
                                  border: `1px solid ${style.border}`,
                                }}
                              >
                                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                  <div
                                    style={{
                                      width: 26,
                                      height: 26,
                                      borderRadius: "6px",
                                      background: style.iconBg,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {style.icon}
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <div className="fw-bold text-uppercase" style={{ fontSize: "0.72rem", color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {channel}
                                    </div>
                                    <div style={{ fontSize: "0.64rem", color: "#64748b" }}>
                                      {channelShare}% of center collections
                                    </div>
                                  </div>
                                </div>

                                <div className="text-end ps-2 flex-shrink-0">
                                  <div className="fw-bold" style={{ fontSize: "0.84rem", color: "#0f172a" }}>
                                    {formatCurrency(amount)}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Card 2: Overall Audit & Balance */}
                    <div
                      className="p-3 rounded-3"
                      style={{
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2.5 border-bottom pb-2">
                        <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#0f172a" }}>
                          ⏳ Center Totals & Audit
                        </span>
                        <span className="badge" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b", fontSize: "0.68rem" }}>
                          Audit Summary
                        </span>
                      </div>

                      {(() => {
                        const totalExpectedLate =
                          Number(center.term_expected_latefee_amount || 0) +
                          Number(center.otherfees_expected_latefee_amount || 0);
                        const totalPaidLate =
                          Number(center.term_latefee_paid_amount || 0) +
                          Number(center.otherfees_latefee_paid_amount || 0);
                        const totalLateRefund =
                          Number(center.term_late_refund_amount || 0) +
                          Number(center.otherfees_late_refund_amount || 0);
                        const totalFeeRefund =
                          Number(center.term_refund_amount || 0) +
                          Number(center.otherfees_refund_amount || 0);
                        const totalUnpaidLate = Math.max(0, totalExpectedLate - totalPaidLate - totalLateRefund);

                        return (
                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#475569" }}>Total Amount Expected</span>
                              <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(totalAmt)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "#16a34a", fontWeight: 600 }}>Total Collected (Paid)</span>
                              <span className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(collected)}</span>
                            </div>

                            {totalExpectedLate > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: COLOR_INDIGO }}>Late Fee (Paid / Unpaid)</span>
                                <span className="fw-bold" style={{ color: COLOR_INDIGO }}>
                                  {formatCurrency(totalPaidLate)} / {formatCurrency(totalUnpaidLate)}
                                </span>
                              </div>
                            )}

                            {Number(center.term_expected_discount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Discounts (Concessions)</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(center.term_expected_discount)}</span>
                              </div>
                            )}

                            {totalFeeRefund > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Fee Refunds Processed</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(totalFeeRefund)}</span>
                              </div>
                            )}

                            {totalLateRefund > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "#475569" }}>Late Fee Refunds Processed</span>
                                <span className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(totalLateRefund)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "#e2e8f0" }}>
                              <span className="fw-bold" style={{ color: Math.max(0, totalAmt - collected) > 0 ? "#dc2626" : "#16a34a" }}>Outstanding Balance</span>
                              <span className="fw-bold" style={{ color: Math.max(0, totalAmt - collected) > 0 ? "#dc2626" : "#16a34a", fontSize: "0.88rem" }}>
                                {formatCurrency(Math.max(0, totalAmt - collected))}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </Col>

                  {/* 3. Direct Fees (Observation & Registration) */}
                  <Col xs={12}>
                    <div
                      className="p-3 rounded-3"
                      style={{
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                      }}
                    >
                      <div className="fw-bold mb-2 pb-1 border-bottom" style={{ fontSize: "0.82rem", color: "#0f172a" }}>
                        🩺 Observation & Registration Direct Fees
                      </div>
                      <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                        <div className="d-flex justify-content-between py-1 px-1">
                          <span style={{ color: "#475569" }}>Observation Fees Paid</span>
                          <span className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(center.observation_paid_amount || 0)}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1 px-1">
                          <span style={{ color: "#475569" }}>Registration Fees Paid</span>
                          <span className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(center.registeration_paid_amount || 0)}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Collapse>
          </Card>
        );
      })}

      {/* View All Centers Button */}
      {centers.length > 5 && (
        <div className="text-center mt-3 mb-2">
          <Button
            color="light"
            className="px-4 py-2 fw-semibold"
            style={{
              borderRadius: "20px",
              border: "1px solid #cbd5e1",
              color: "#334155",
              fontSize: "0.84rem",
              background: "#ffffff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            }}
            onClick={() => setShowAllCenters((prev) => !prev)}
          >
            {showAllCenters ? "Show Fewer Centers" : `View All Centers (${centers.length})`}
          </Button>
        </div>
      )}
    </Container>
  );
};

// ─── Component: FeeBreakdownCard (Complete Executive Redesign) ────────────────
const FeeBreakdownCard = ({
  title,
  total,
  paid,
  historicalPaid = 0,
  refund = 0,
  discount = 0,
  skipped = 0,
  lateTotal = 0,
  latePaid = 0,
  lateRefund = 0,
  balance,
  themeColor = "#16a34a",
  icon,
}) => {
  const displayLatePaid = Math.max(0, Number(latePaid));
  const lateUnpaid = Math.max(0, Number(lateTotal) - displayLatePaid - Number(lateRefund));
  const computedBalance = Math.max(
    0,
    Number(total) - Number(paid) - Number(refund) - displayLatePaid - Number(discount)
  );
  const displayBalance = balance !== undefined ? Math.max(0, Number(balance)) : computedBalance;
  const pctPaid = calculatePercentage(paid, total);
  const isSettled = displayBalance <= 0;

  return (
    <Card
      className="border-0 shadow-sm h-100 mb-0"
      style={{
        borderRadius: "14px",
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Card Header */}
      <div
        className="px-3.5 py-2.5 d-flex justify-content-between align-items-center"
        style={{
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          {icon}
          <span className="fw-bold" style={{ fontSize: "0.86rem", color: TEXT_DARK }}>
            {title}
          </span>
        </div>
        <span
          style={{
            backgroundColor: isSettled ? "#f0fdf4" : "#eef2ff",
            color: isSettled ? "#16a34a" : "#4f46e5",
            fontSize: "0.72rem",
            fontWeight: 700,
            border: isSettled ? "1px solid #bbf7d0" : "1px solid #c7d2fe",
            padding: "3px 10px",
            borderRadius: "20px",
          }}
        >
          {pctPaid}% Collected
        </span>
      </div>

      <CardBody className="p-3.5 d-flex flex-column justify-content-between">
        {/* Progress Bar */}
        <div className="mb-3">
          <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.7rem", color: "#64748b" }}>
            <span>Collection Progress</span>
            <span className="fw-semibold" style={{ color: isSettled ? "#16a34a" : "#4f46e5" }}>{pctPaid}%</span>
          </div>
          <Progress
            value={pctPaid}
            color={isSettled ? "success" : "primary"}
            style={{ height: "5px", borderRadius: "3px", backgroundColor: "#f1f5f9" }}
          />
        </div>

        {/* 3-Stat Summary Pill Strip */}
        <div
          className="d-flex align-items-center justify-content-between p-2 mb-3 rounded"
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            fontSize: "0.76rem",
          }}
        >
          <div className="text-center flex-fill">
            <div style={{ fontSize: "0.66rem", color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Billed</div>
            <div className="fw-bold" style={{ color: "#0f172a" }}>{formatCurrency(total)}</div>
          </div>
          <div className="border-start text-center flex-fill ps-2">
            <div style={{ fontSize: "0.66rem", color: "#16a34a", textTransform: "uppercase", fontWeight: 600 }}>Paid</div>
            <div className="fw-bold" style={{ color: "#16a34a" }}>{formatCurrency(paid)}</div>
          </div>
          <div className="border-start text-center flex-fill ps-2">
            <div style={{ fontSize: "0.66rem", color: isSettled ? "#16a34a" : "#dc2626", textTransform: "uppercase", fontWeight: 600 }}>Balance</div>
            <div className="fw-bold" style={{ color: isSettled ? "#16a34a" : "#dc2626" }}>{formatCurrency(displayBalance)}</div>
          </div>
        </div>

        {/* Line Items List */}
        <div className="d-flex flex-column gap-1 mb-3" style={{ fontSize: "0.76rem" }}>
          {Number(historicalPaid) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "#f8fafc" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#64748b" }}></span>
                <span style={{ color: "#475569" }}>Historical Paid</span>
              </div>
              <span className="fw-semibold" style={{ color: "#64748b" }}>{formatCurrency(historicalPaid)}</span>
            </div>
          )}

          {Number(skipped) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "#eef2ff" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4f46e5" }}></span>
                <span style={{ color: "#4f46e5" }}>Skipped Term Amount (Included)</span>
              </div>
              <span className="fw-semibold" style={{ color: "#4f46e5" }}>{formatCurrency(skipped)}</span>
            </div>
          )}

          {Number(discount) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "#fef2f2" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc2626" }}></span>
                <span style={{ color: "#475569" }}>Discount Deduction</span>
              </div>
              <span className="fw-semibold" style={{ color: "#0f172a" }}>{formatCurrency(discount)}</span>
            </div>
          )}

          {Number(refund) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "#fef2f2" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc2626" }}></span>
                <span style={{ color: "#475569" }}>Refund Amount</span>
              </div>
              <span className="fw-semibold" style={{ color: "#0f172a" }}>{formatCurrency(refund)}</span>
            </div>
          )}
        </div>

        {/* Late Fee Nested Container */}
        {Number(lateTotal) > 0 && (
          <div
            className="p-2.5 rounded mb-3"
            style={{
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              fontSize: "0.74rem",
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-1.5">
              <span className="fw-bold" style={{ color: "#ea580c" }}>
                ⏳ Late Fee Summary
              </span>
              <span className="fw-bold" style={{ color: "#ea580c" }}>
                Total: {formatCurrency(lateTotal)}
              </span>
            </div>
            <div className="d-flex align-items-center justify-content-between pt-1 border-top" style={{ borderColor: "#fed7aa" }}>
              <div className="d-flex align-items-center gap-1">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a" }}></span>
                <span style={{ color: "#475569" }}>Paid:</span>
                <strong style={{ color: "#16a34a" }}>{formatCurrency(displayLatePaid)}</strong>
              </div>
              <div className="d-flex align-items-center gap-1">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: lateUnpaid > 0 ? "#dc2626" : "#64748b" }}></span>
                <span style={{ color: "#475569" }}>Unpaid:</span>
                <strong style={{ color: lateUnpaid > 0 ? "#dc2626" : "#64748b" }}>{formatCurrency(lateUnpaid)}</strong>
              </div>
              {Number(lateRefund) > 0 && (
                <div className="d-flex align-items-center gap-1">
                  <span style={{ color: "#475569" }}>Refund:</span>
                  <strong style={{ color: "#0f172a" }}>{formatCurrency(lateRefund)}</strong>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Final Balance Footer Row */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
          style={{
            background: isSettled ? "#f0fdf4" : "#fef2f2",
            border: isSettled ? "1px solid #bbf7d0" : "1px solid #fecaca",
          }}
        >
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              color: isSettled ? "#16a34a" : "#dc2626",
              letterSpacing: "0.4px",
            }}
          >
            {isSettled ? "✓ Fully Settled" : "Remaining Balance"}
          </span>
          <span
            style={{
              fontSize: "0.92rem",
              fontWeight: 800,
              color: isSettled ? "#16a34a" : "#dc2626",
              fontFamily: "monospace, sans-serif",
            }}
          >
            {formatCurrency(displayBalance)}
          </span>
        </div>
      </CardBody>
    </Card>
  );
};

// ─── Component: InfoCard (Redesigned) ────────────────────────────────────────
const InfoCard = ({ title, amount, icon }) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    }}
  >
    <div className="d-flex align-items-center gap-2.5">
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          background: "#f8fafc",
          color: "#64748b",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>
          {title}
        </div>
        <div style={{ fontSize: "0.68rem", color: "#64748b" }}>
          One-time direct fee
        </div>
      </div>
    </div>
    <div className="text-end">
      <div className="fw-bold" style={{ fontSize: "0.92rem", color: "#16a34a", fontFamily: "monospace, sans-serif" }}>
        {formatCurrency(amount)}
      </div>
      <span
        style={{
          fontSize: "0.62rem",
          fontWeight: 700,
          padding: "1px 6px",
          borderRadius: "4px",
          background: Number(amount) > 0 ? "#f0fdf4" : "#f1f5f9",
          color: Number(amount) > 0 ? "#16a34a" : "#64748b",
        }}
      >
        {Number(amount) > 0 ? "Paid" : "None"}
      </span>
    </div>
  </div>
);

export default AcademicFeesDashboard;

